import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ResidentAppError } from '../../../common/error'

export const useOpenFile = (
  getSignedUrl: () => Promise<string | undefined>
) => {
  const { t } = useTranslation(['common'])
  const [isOpening, setIsOpening] = useState(false)
  const [hasOpenError, setHasOpenError] = useState(false)

  const openFileInNewTab = useCallback(async (): Promise<void> => {
    const newTab = window.open('about:blank', '_blank')
    if (!newTab) {
      const signedUrl = await getSignedUrl()
      if (!signedUrl) {
        throw ResidentAppError.ClientError('FILE_FAILED_TO_OPEN', {
          message: 'Signed URL not received for the same-tab fallback.',
        })
      }
      window.location.assign(signedUrl)
      return
    }

    try {
      newTab.document.title = t('common:openFile.loadingTitle')
      newTab.document.body.innerHTML = `<p>${t('common:openFile.loading')}</p>`
    } catch {
      /* noop */
    }

    const LOAD_TIMEOUT_MS = 8000
    const loadingTimeoutId = window.setTimeout(() => {
      try {
        newTab.document.body.innerHTML = `<p>${t('common:openFile.loadingSlow')}</p>`
      } catch {
        /* noop */
      }
    }, LOAD_TIMEOUT_MS)

    try {
      const signedUrl = await getSignedUrl()
      if (!signedUrl) {
        throw ResidentAppError.ClientError('FILE_FAILED_TO_LOAD', {
          message: 'Signed URL not received from server.',
        })
      }

      // Omit credentials if different origin (avoid CORS errors with presigned URLs)
      let creds: RequestCredentials = 'include'
      try {
        const su = new URL(signedUrl)
        const here = new URL(window.location.href)
        if (su.origin !== here.origin) creds = 'omit'
      } catch {
        /* noop */
      }

      let res: Response
      try {
        res = await fetch(signedUrl, {
          mode: 'cors',
          credentials: creds,
        })
      } catch {
        newTab.location.replace(new URL(signedUrl, window.location.href).href)
        return
      }
      if (!res.ok) {
        throw ResidentAppError.ClientError('FILE_FAILED_TO_LOAD', {
          message: `Failed to load file (HTTP ${res.status}).`,
        })
      }

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)

      // Navigate to the new tab
      newTab.location.replace(objectUrl)

      // Cleanup: onload + fallback delay + unload
      newTab.onload = () => {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          /* noop */
        }
      }
      const revokeTimer = window.setTimeout(() => {
        try {
          URL.revokeObjectURL(objectUrl)
        } catch {
          /* noop */
        }
      }, 15000)
      newTab.addEventListener(
        'unload',
        () => {
          try {
            URL.revokeObjectURL(objectUrl)
            window.clearTimeout(revokeTimer)
          } catch {
            /* noop */
          }
        },
        { once: true }
      )
    } catch (err) {
      try {
        newTab.document.title = t('common:openFile.errorTitle')
        newTab.document.body.innerHTML = `<p>${t('common:openFile.failed')}</p>`
      } catch {
        /* noop */
      }
      throw err
    } finally {
      window.clearTimeout(loadingTimeoutId)
    }
  }, [getSignedUrl, t])

  const openFile = useCallback(async (): Promise<void> => {
    setIsOpening(true)
    setHasOpenError(false)
    try {
      await openFileInNewTab()
    } catch {
      setHasOpenError(true)
    } finally {
      setIsOpening(false)
    }
  }, [openFileInNewTab])

  return { openFile, isOpening, hasOpenError }
}
